import { UnitLabel } from '@/shared/config/config'
import {
  Button,
  Alert,
  GenericDialog,
  Table,
  TableCell,
  TableRow,
  TableColumnHeader,
  DataBlock,
  CheckboxGroup,
  NumericInputGroup
} from '@/common'
import { useState, useCallback, useEffect, useMemo } from 'react'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { TrashIcon } from '@/common/components/icons'
import CementClinkerRawMealCompositionCalculatorPanel, {
  CementClinkerRawMealComposition
} from './CementClinkerRawMealCompositionCalculatorPanel'
import useI18n from '@/shared/hooks/useI18n'

function calcualteCkdEmissionFactorFromMeasuredValues(
  degreeOfCkdCalcination: string,
  installationSpecificKilnDustEmissionFactor: string
) {
  const d = Number(degreeOfCkdCalcination) / 100
  const efCli = Number(installationSpecificKilnDustEmissionFactor)
  const clinkerEmissionRatio = efCli / (1 + efCli)
  const ratioTimesD = clinkerEmissionRatio * d
  const efCkd = ratioTimesD / (1 - ratioTimesD)
  return efCkd
}

type CementClinkerInputBasedArtifacts = {
  carbonateEmissions: CementClinkerRawMealComposition[]
  kilnDustUseMeasuredValue: boolean
  kilnDustEmissionFactor: string
  cementDustTonnes: string
  installationSpecificKilnDustEmissionFactor: string
  degreeOfCkdCalcination: string
}

const getInitialState = (): CementClinkerInputBasedArtifacts => {
  return {
    carbonateEmissions: [],
    kilnDustUseMeasuredValue: false,
    kilnDustEmissionFactor: '0.525',
    cementDustTonnes: '',
    installationSpecificKilnDustEmissionFactor: '',
    degreeOfCkdCalcination: ''
  }
}

export default function CementClinkerInputBasedCalculatorDialog({
  isOpen,
  close,
  onSubmit,
  removeEmissionRecord,
  initialValues,
  emissionRecordId
}: {
  isOpen: boolean
  close: () => void
  initialValues: CementClinkerInputBasedArtifacts
  onSubmit: (emission: number, artifacts: CementClinkerInputBasedArtifacts) => void
  removeEmissionRecord: (id: string) => void
  emissionRecordId?: string
}) {
  const { t, numberFormatter2Decimals } = useI18n()
  const { checkPermission } = usePermissions()
  const canEditReport = checkPermission('emissionReport', 'update')
  const [state, setState] = useState(initialValues || getInitialState())
  const carbonates = state?.carbonateEmissions || []
  const [isFormShowing, setIsFormShowing] = useState(false)
  const carbonatesEmission = carbonates.reduce((acc, emission) => acc + emission.emission, 0)
  const calculatedCkdEmissionFactor = calcualteCkdEmissionFactorFromMeasuredValues(
    state.degreeOfCkdCalcination,
    state.installationSpecificKilnDustEmissionFactor
  )

  const ckdEmission =
    Number(state.cementDustTonnes) *
    (state.kilnDustUseMeasuredValue
      ? calculatedCkdEmissionFactor
      : Number(state.kilnDustEmissionFactor))

  const totalEmission = carbonatesEmission + ckdEmission
  const closeRawMealCompositionCalculator = useCallback(() => {
    setIsFormShowing(false)
  }, [])

  const onClose = useCallback(() => {
    close()
    setState(getInitialState())
  }, [close])

  const removeRawMealCompositionEmission = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      carbonateEmissions: prev.carbonateEmissions.filter((_, i) => i !== index)
    }))
  }, [])

  const onAddClick = useCallback(() => {
    if (!carbonatesEmission && !ckdEmission) {
      return
    }
    const artifacts = {
      carbonateEmissions: carbonates,
      kilnDustUseMeasuredValue: state.kilnDustUseMeasuredValue,
      kilnDustEmissionFactor: state.kilnDustEmissionFactor,
      installationSpecificKilnDustEmissionFactor: state.installationSpecificKilnDustEmissionFactor,
      degreeOfCkdCalcination: state.degreeOfCkdCalcination,
      cementDustTonnes: state.cementDustTonnes
    }
    onSubmit(carbonatesEmission + ckdEmission, artifacts)
    onClose()
  }, [onClose, onSubmit, carbonatesEmission, ckdEmission])

  const addRawMealCompositionEmission = useCallback(
    (rawMealComposition: CementClinkerRawMealComposition) => {
      const newCarbonateEmissions = [...(state?.carbonateEmissions || []), rawMealComposition]
      setState({
        ...state,
        carbonateEmissions: newCarbonateEmissions
      })
      closeRawMealCompositionCalculator()
    },
    [closeRawMealCompositionCalculator, state]
  )

  const onRemoveClick = useCallback(() => {
    removeEmissionRecord(emissionRecordId!)
  }, [emissionRecordId, removeEmissionRecord])

  useEffect(() => {
    if (initialValues) {
      setState(initialValues)
    }
  }, [initialValues])

  const isAddEnabled = useMemo(() => {
    const hasEmission = totalEmission > 0
    if (state.kilnDustUseMeasuredValue) {
      return (
        hasEmission &&
        state.installationSpecificKilnDustEmissionFactor &&
        state.degreeOfCkdCalcination
      )
    }
    return hasEmission && state.kilnDustEmissionFactor && state.cementDustTonnes
  }, [state, totalEmission])

  const formattedTotalEmission = useMemo(() => {
    return numberFormatter2Decimals(totalEmission)
  }, [totalEmission])

  const formattedCkdEmission = useMemo(() => {
    return numberFormatter2Decimals(ckdEmission)
  }, [ckdEmission])

  const formattedCarbonatesEmission = useMemo(() => {
    return numberFormatter2Decimals(carbonatesEmission)
  }, [carbonatesEmission])

  return (
    <GenericDialog
      size="2xl"
      title={t('report:cementClinkerInputBasedCalculatorDialog.title')}
      isOpen={isOpen}
      close={onClose}
    >
      <div className="space-y-4">
        <div>
          <CementClinkerRawMealCompositionCalculatorPanel
            isOpen={isFormShowing}
            close={() => setIsFormShowing(false)}
            onSubmit={addRawMealCompositionEmission}
          />
          <div className="space-y-4 flex flex-col">
            <Alert type="info">{t('report:cementClinkerInputBasedCalculatorDialog.alert')}</Alert>
            <h4 className="Artem-dark-blue font-semibold">
              {t(
                'report:cementClinkerInputBasedCalculatorDialog.sections.carbonatesEmission.title'
              )}
            </h4>
            <Table>
              <thead>
                <TableRow>
                  <TableColumnHeader className="w-1/5">
                    {t('report:common:fields.carbonate.label')}
                  </TableColumnHeader>
                  <TableColumnHeader className="w-1/5">
                    {t('report:common:fields.emissionFactor.label')}
                  </TableColumnHeader>
                  <TableColumnHeader className="w-1/5">
                    {t('report:common:fields.activityData.label')}
                  </TableColumnHeader>
                  <TableColumnHeader className="w-1/5">
                    {t('report:common:emission')}
                  </TableColumnHeader>
                  <TableColumnHeader className="w-1/5" />
                </TableRow>
              </thead>
              <tbody>
                {carbonates.map((emission, index) => (
                  <TableRow key={index}>
                    <TableCell>{emission.carbonate}</TableCell>
                    <TableCell>
                      {emission.emissionFactor} {UnitLabel.tCO2PerT}
                    </TableCell>
                    <TableCell>
                      {emission.activityData} {UnitLabel.t}
                    </TableCell>
                    <TableCell>
                      {emission.emission} {UnitLabel.tCO2}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        sizeType="sm"
                        variant="danger"
                        icon={<TrashIcon />}
                        onClick={() => removeRawMealCompositionEmission(index)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4}></TableCell>
                  <TableCell colSpan={1}>
                    <Button
                      className="w-full"
                      variant="txt-default"
                      onClick={() => setIsFormShowing(true)}
                    >
                      {t('common:actions.add')}
                    </Button>
                  </TableCell>
                </TableRow>
              </tbody>
            </Table>
          </div>
        </div>
        <section className="space-y-2">
          <header className="flex justify-between items-center">
            <h4 className="Artem-dark-blue font-semibold">
              {t('report:cementClinkerInputBasedCalculatorDialog.sections.ckdEmission.title')}
            </h4>
            <CheckboxGroup
              id="kilnDustUseMeasuredValue"
              checked={state.kilnDustUseMeasuredValue}
              label="Use measured value"
              onChange={() => {
                setState(prev => ({
                  ...prev,
                  kilnDustUseMeasuredValue: !prev.kilnDustUseMeasuredValue
                }))
              }}
            />
          </header>
          <div className="space-y-4">
            <div className="space-y-4">
              <NumericInputGroup
                id="kilnDustEmissionFactor"
                label={t(
                  'report:cementClinkerInputBasedCalculatorDialog.sections.ckdEmission.fields.kilnDustEmissionFactor.label'
                )}
                suffix={UnitLabel.tCO2PerT}
                disabled
                value={
                  state.kilnDustUseMeasuredValue
                    ? (calculatedCkdEmissionFactor || '').toString()
                    : state.kilnDustEmissionFactor
                }
                onChange={evt =>
                  setState(prev => ({
                    ...prev,
                    kilnDustEmissionFactor: evt.target.value
                  }))
                }
              />
              <NumericInputGroup
                id="cementDustTonnes"
                label={t(
                  'report:cementClinkerInputBasedCalculatorDialog.sections.ckdEmission.fields.cementDustTonnes.label'
                )}
                value={state.cementDustTonnes}
                suffix={UnitLabel.t}
                onChange={evt =>
                  setState(prev => ({
                    ...prev,
                    cementDustTonnes: evt.target.value
                  }))
                }
              />
              {state.kilnDustUseMeasuredValue && (
                <>
                  <NumericInputGroup
                    id="installationSpecificKilnDustEmissionFactor"
                    label={t(
                      'report:cementClinkerInputBasedCalculatorDialog.sections.ckdEmission.fields.installationSpecificKilnDustEmissionFactor.label'
                    )}
                    value={state.installationSpecificKilnDustEmissionFactor}
                    suffix={UnitLabel.tCO2PerT}
                    onChange={evt =>
                      setState(prev => ({
                        ...prev,
                        installationSpecificKilnDustEmissionFactor: evt.target.value
                      }))
                    }
                  />
                  <NumericInputGroup
                    id="degreeOfCkdCalcination"
                    label={t(
                      'report:cementClinkerInputBasedCalculatorDialog.sections.ckdEmission.fields.degreeOfCkdCalcination.label'
                    )}
                    value={state.degreeOfCkdCalcination}
                    suffix="%"
                    hint={t(
                      'report:cementClinkerInputBasedCalculatorDialog.sections.ckdEmission.fields.degreeOfCkdCalcination.hint'
                    )}
                    onChange={evt =>
                      setState(prev => ({
                        ...prev,
                        degreeOfCkdCalcination: evt.target.value
                      }))
                    }
                  />
                </>
              )}
            </div>
          </div>
        </section>
        <section className="grid grid-cols-3 gap-4">
          <DataBlock
            data={`${formattedCarbonatesEmission} ${UnitLabel.tCO2}`}
            label={t('report:cementClinkerInputBasedCalculatorDialog:carbonatesEmission')}
          />
          <DataBlock
            data={`${formattedCkdEmission} ${UnitLabel.tCO2}`}
            label={t('report:cementClinkerInputBasedCalculatorDialog:kilnDustEmission')}
          />
          <DataBlock
            data={`${formattedTotalEmission} ${UnitLabel.tCO2}`}
            label={t('report:cementClinkerInputBasedCalculatorDialog:totalEmission')}
          />
        </section>
      </div>
      <footer className="flex justify-between mt-4">
        <div className="flex justify-end">
          <Button
            className="min-w-24"
            onClick={onRemoveClick}
            disabled={isFormShowing}
            variant="danger"
          >
            {t('common:actions.remove')}
          </Button>
        </div>
        <div className="flex justify-end space-x-4">
          <Button className="min-w-24" onClick={close} disabled={isFormShowing} variant="neutral">
            {t('common:actions.cancel')}
          </Button>
          <Button
            className="min-w-24"
            disabled={!isAddEnabled || !canEditReport}
            onClick={onAddClick}
          >
            {t('common:actions.add')}
          </Button>
        </div>
      </footer>
    </GenericDialog>
  )
}
